/**
 * Notifications API: list, mark as read, mark all as read.
 *
 * Legacy Ionic marks one notification read via **GET** `notifications/update?customer_id=&notification_id=`
 * (`docs/oldionicsrc/app/notifications/notifications.page.ts`). This route returns **405** for POST; mark-all uses the same GET endpoint with `mark_all`.
 */
import { apiGet } from '../../../shared/api/client';
import { getAuthStore } from '../../../shared/auth/store';
import { endpoints } from '../../../shared/config/endpoints';
import type { Notification } from './apiTypes';

/** Normalized list + server unread count (React Query cache shape). */
export type NotificationsListResult = {
  notifications: Notification[];
  unread_count: number;
};

/** Backend requires `customer_id` (same as legacy Ionic `?customer_id=${user.id}`). */
function customerIdOrNull(): number | null {
  const id = getAuthStore().user?.id;
  return typeof id === 'number' && id > 0 ? id : null;
}

export type { Notification };

/**
 * Legacy API uses `is_read == 0` in Ionic templates; some payloads use strings `"0"` / `"1"`.
 */
export function rawNotificationIsRead(raw: { is_read?: unknown; read?: unknown }): boolean {
  const classify = (v: unknown): boolean | undefined => {
    if (v === true || v === 1 || v === '1') {
      return true;
    }
    if (v === false || v === 0 || v === '0') {
      return false;
    }
    return undefined;
  };
  const from_is_read = classify(raw.is_read);
  if (from_is_read !== undefined) {
    return from_is_read;
  }
  const from_read = classify(raw.read);
  if (from_read !== undefined) {
    return from_read;
  }
  return false;
}

function countUnreadRaw(rawList: unknown[]): number {
  let n = 0;
  for (const raw of rawList) {
    if (raw == null || typeof raw !== 'object') {
      continue;
    }
    if (!rawNotificationIsRead(raw as { is_read?: unknown; read?: unknown })) {
      n += 1;
    }
  }
  return n;
}

/**
 * Supports current API `{ data: { notifications, unread_count } }` and legacy `data` array.
 */
function extractListPayload(res: unknown): { raw_list: unknown[]; unread_count: number } {
  const data = res != null && typeof res === 'object' ? (res as { data?: unknown }).data : undefined;
  if (data != null && typeof data === 'object' && !Array.isArray(data)) {
    const notifications = (data as { notifications?: unknown }).notifications;
    const unread_count = (data as { unread_count?: unknown }).unread_count;
    if (Array.isArray(notifications)) {
      return {
        raw_list: notifications,
        unread_count:
          typeof unread_count === 'number' && Number.isFinite(unread_count)
            ? Math.max(0, Math.floor(unread_count))
            : countUnreadRaw(notifications),
      };
    }
  }
  if (Array.isArray(data)) {
    return {
      raw_list: data,
      unread_count: countUnreadRaw(data),
    };
  }
  return { raw_list: [], unread_count: 0 };
}

function normalizeNotification(raw: Record<string, unknown>): Notification & { is_read?: boolean } {
  const read = rawNotificationIsRead(raw);
  const content = raw.content != null ? String(raw.content) : '';
  const title_raw = raw.title != null ? String(raw.title).trim() : '';
  const message_raw = raw.message != null ? String(raw.message) : '';
  return {
    id: Number(raw.id),
    customer_id: raw.customer_id != null ? Number(raw.customer_id) : undefined,
    title: title_raw,
    message: message_raw !== '' ? message_raw : content,
    content: content !== '' ? content : undefined,
    type: (raw.type as Notification['type']) ?? 'general',
    read,
    is_read: read,
    created_at:
      raw.created_at != null
        ? String(raw.created_at)
        : raw.timestamp != null
          ? String(raw.timestamp)
          : '',
    date: raw.date != null ? String(raw.date) : undefined,
    time: raw.time != null ? String(raw.time) : undefined,
    is_redirect: raw.is_redirect === true || raw.is_redirect === 1,
    sent_by_user:
      raw.sent_by_user != null && typeof raw.sent_by_user === 'object'
        ? (raw.sent_by_user as Record<string, unknown>)
        : undefined,
    data: raw.data != null && typeof raw.data === 'object' ? (raw.data as Record<string, unknown>) : undefined,
  };
}

/**
 * Fetch in-app notifications for the current user.
 */
export async function fetchNotifications(): Promise<NotificationsListResult> {
  const customer_id = customerIdOrNull();
  if (customer_id == null) {
    return { notifications: [], unread_count: 0 };
  }
  const res = await apiGet<unknown>(endpoints.notifications, {
    customer_id,
  });
  const { raw_list, unread_count } = extractListPayload(res);
  const notifications = raw_list
    .map(item =>
      normalizeNotification(item != null && typeof item === 'object' ? (item as Record<string, unknown>) : {}),
    )
    .filter(n => Number.isFinite(n.id));
  return { notifications, unread_count };
}

/**
 * Mark a single notification as read.
 */
export async function markNotificationAsRead(notification_id: number): Promise<void> {
  const customer_id = customerIdOrNull();
  if (customer_id == null) {
    return;
  }
  await apiGet<unknown>(endpoints.notificationsUpdate, {
    customer_id,
    notification_id,
  });
}

/**
 * Mark all notifications as read.
 */
export async function markAllNotificationsAsRead(): Promise<void> {
  const customer_id = customerIdOrNull();
  if (customer_id == null) {
    return;
  }
  await apiGet<unknown>(endpoints.notificationsUpdate, {
    customer_id,
    mark_all: 1,
  });
}
